import { JobMatchPoolerUser } from '../app/jobs/job-edit/job-edit.types';
import { Shift } from 'server/shared/models/shift';
import { addHours, addMinutes, addDays, startOfDay } from 'date-fns';
import { MultiDay } from 'src/types/job/MultiDay';

export type RestViolationResult = {
  user: JobMatchPoolerUser;
  firstShift: {
    start: Date;
    end: Date;
  };
  secondShift: {
    start: Date;
    end: Date;
  };
  startDate: string;
  endDate: string;
  dateString: string;
  // may be 1 or 2 affected days (by uuid because the same day may appear multiple times)
  multiDayUuids: string[];
};

// violations by multiDayUuid
export type ViolationsPerDayUuid = Map<
  string,
  {
    restViolations: RestViolationResult[];
    overlaps: RestViolationResult[];
  }
>;

export function checkMultiDayRest(
  multiDays: Pick<
    MultiDay,
    'date' | 'shifts' | 'inviteUsers' | 'directAcceptUsers' | 'uuid'
  >[],
  desiredRestHours: number,
): ViolationsPerDayUuid {
  const userWorkShifts = createUserWorkShifts(multiDays);
  const overlaps = checkRestViolations(userWorkShifts, 0);
  const restViolations = checkRestViolations(userWorkShifts, desiredRestHours);

  const overlapsByUuid = new Map();
  overlaps.forEach((violation) => {
    violation.multiDayUuids.forEach((uuid) => {
      if (!overlapsByUuid.has(uuid)) {
        overlapsByUuid.set(uuid, []);
      }
      overlapsByUuid.get(uuid).push(violation);
    });
  });

  const restViolationsByUuid = new Map();
  restViolations.forEach((violation) => {
    violation.multiDayUuids.forEach((uuid) => {
      if (!restViolationsByUuid.has(uuid)) {
        restViolationsByUuid.set(uuid, []);
      }
      restViolationsByUuid.get(uuid).push(violation);
    });
  });

  const allUuids = [
    ...new Set([...overlapsByUuid.keys(), ...restViolationsByUuid.keys()]),
  ];

  return new Map(
    allUuids.map((uuid) => {
      const dayOverlaps: RestViolationResult[] = overlapsByUuid.get(uuid) || [];
      const dayRestViolations: RestViolationResult[] =
        restViolationsByUuid.get(uuid) || [];

      // Filter out rest violations that are already in overlaps
      const filteredRestViolations = dayRestViolations.filter(
        (violation) =>
          !dayOverlaps.some(
            (overlap) =>
              // same user
              overlap.user._id === violation.user._id &&
              // same shifts
              overlap.firstShift.start.getTime() ===
                violation.firstShift.start.getTime() &&
              overlap.secondShift.start.getTime() ===
                violation.secondShift.start.getTime() &&
              // same affected dayUuids
              violation.multiDayUuids.every((uuid) =>
                overlap.multiDayUuids.includes(uuid),
              ),
          ),
      );

      return [
        uuid,
        {
          overlaps: dayOverlaps,
          restViolations: filteredRestViolations,
        },
      ];
    }),
  );
}

function createUserWorkShifts(
  multiDays: Pick<MultiDay, 'date' | 'shifts' | 'directAcceptUsers' | 'uuid'>[],
): {
  user: JobMatchPoolerUser;
  workShifts: { start: Date; end: Date; date: string; multiDayUuid: string }[];
}[] {
  const userWorkShiftsMap = createUserWorkShiftsMap(multiDays);
  return Array.from(userWorkShiftsMap.values());
}

function createUserWorkShiftsMap(
  multiDays: Pick<MultiDay, 'date' | 'shifts' | 'directAcceptUsers' | 'uuid'>[],
): Map<
  string,
  {
    user: JobMatchPoolerUser;
    workShifts: {
      start: Date;
      end: Date;
      date: string;
      multiDayUuid: string;
    }[];
  }
> {
  return multiDays.reduce((acc, day) => {
    day.directAcceptUsers.forEach((user) => {
      if (!acc.has(user._id)) {
        acc.set(user._id, { user, workShifts: [] });
      }
      const workShifts = createWorkShiftsForDay(day);
      acc.get(user._id)!.workShifts.push(...workShifts);
    });
    return acc;
  }, new Map<string, { user: JobMatchPoolerUser; workShifts: { start: Date; end: Date; date: string; multiDayUuid: string }[] }>());
}

function createWorkShiftsForDay(
  day: Pick<MultiDay, 'date' | 'shifts' | 'uuid'>,
): { start: Date; end: Date; date: string; multiDayUuid: string }[] {
  return day.shifts.map((shift) => {
    const { start, end } = createDatesForShift(day.date, shift);
    return { start, end, date: day.date, multiDayUuid: day.uuid };
  });
}

function checkRestViolations(
  userWorkShifts: {
    user: JobMatchPoolerUser;
    workShifts: {
      start: Date;
      end: Date;
      date: string;
      multiDayUuid: string;
    }[];
  }[],
  desiredRestHours: number,
): RestViolationResult[] {
  return userWorkShifts.reduce(
    (allViolations: RestViolationResult[], { user, workShifts }) => {
      const sortedShifts = workShifts.sort(
        (a, b) => a.start.getTime() - b.start.getTime(),
      );

      const userViolations = sortedShifts.reduce(
        (violations: RestViolationResult[], currentShift, index, shifts) => {
          if (index === 0) return violations;

          const previousShift = shifts[index - 1];
          const restTime =
            (currentShift.start.getTime() - previousShift.end.getTime()) /
            (1000 * 60 * 60);

          if (restTime < desiredRestHours) {
            violations.push({
              user,
              firstShift: {
                start: previousShift.start,
                end: previousShift.end,
              },
              secondShift: {
                start: currentShift.start,
                end: currentShift.end,
              },
              startDate: previousShift.date,
              endDate: currentShift.date,
              dateString: currentShift.date,
              multiDayUuids: Array.from(
                new Set([
                  previousShift.multiDayUuid,
                  currentShift.multiDayUuid,
                ]),
              ),
            });
          }

          return violations;
        },
        [],
      );

      return [...allViolations, ...userViolations];
    },
    [],
  );
}

function createDatesForShift(
  dateString: string,
  shift: Shift,
): { start: Date; end: Date } {
  const baseDate = new Date(dateString);

  const start = createDateTime(shift.from, baseDate);
  let end = createDateTime(shift.to, baseDate);

  // Handle overnight shifts
  if (shift.to <= shift.from) {
    end = addDays(end, 1);
  }

  return { start, end };
}

function createDateTime(time: number, date: Date) {
  const hours = Math.floor(time / 100);
  const minutes = time % 100;
  return addMinutes(addHours(startOfDay(date), hours), minutes);
}
